import { useState } from 'react'
import { X } from 'lucide-react'
import { Button, Modal } from '../../core/index.js'
import type { AvailableShift, ShiftPreferences } from '../types.js'
import { NO_SEAT_FILL, publishedShiftsByDay, SEAT_FILL_BASIS_TEXT, SHIFT_TYPE_ORDER, shiftTypeDemand, type PublishedDay, type SeatFill } from './monthReadinessData.js'
import { describeDay, describePeriod, SHIFT_TYPE_LABELS } from './preferencesText.js'

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function Pills({ label, options, value, onChange }: { label: string; options: string[]; value: string | null; onChange: (value: string | null) => void }) {
  if (options.length < 2) {
    return null
  }
  return (
    <div role="radiogroup" aria-label={label} className="flex flex-wrap items-center gap-1">
      <span className="text-[9px] font-bold uppercase tracking-wide text-ink-mid">{label}</span>
      {[null, ...options].map((option) => (
        <button key={option ?? 'all'} type="button" role="radio" aria-checked={value === option} onClick={() => onChange(option)} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${value === option ? 'border-marine bg-marine text-white' : 'border-marine/30 text-ink-mid hover:border-marine'}`}>
          {option ?? 'All'}
        </button>
      ))}
    </div>
  )
}

/** A seat at a glance: filled solid marine, open an empty coral outline - the key above the calendar says so. */
const SEAT_FILLED = 'bg-marine border-marine'
const SEAT_OPEN = 'bg-white border-coral'
/** Past this many seats of a type in a day, the squares give way to a count. */
const MAX_SQUARES = 6

function SeatSquares({ filled, seats }: { filled: number; seats: number }) {
  if (seats > MAX_SQUARES) {
    return (
      <span className="text-[9px] leading-none">
        <span className="font-bold text-marine">{filled}</span>
        <span className="text-ink-mid">/{seats}</span>
      </span>
    )
  }
  return (
    <span className="flex gap-px" aria-label={`${filled} of ${seats} filled`}>
      {Array.from({ length: seats }, (_, index) => (
        <span key={index} className={`h-1.5 w-1.5 rounded-sm border ${index < filled ? SEAT_FILLED : SEAT_OPEN}`} />
      ))}
    </span>
  )
}

function SeatKey({ fill }: { fill: SeatFill }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-ink-mid">
      <span className="flex items-center gap-1">
        <span className={`h-2 w-2 rounded-sm border ${SEAT_FILLED}`} /> Filled
      </span>
      <span className="flex items-center gap-1">
        <span className={`h-2 w-2 rounded-sm border ${SEAT_OPEN}`} /> Open
      </span>
      <span className="flex items-center gap-1">
        <span className="h-2 w-3 rounded-sm border border-dashed border-coral/60" /> Nothing published
      </span>
      <span className="text-ink-mid/80">{SEAT_FILL_BASIS_TEXT[fill.basis]}</span>
    </div>
  )
}

function DayCell({ day, onOpen }: { day: PublishedDay; onOpen: () => void }) {
  const empty = day.shifts.length === 0
  return (
    <button type="button" onClick={onOpen} disabled={empty} title={empty ? 'Nothing published' : undefined} className={`flex min-h-[54px] flex-col items-start gap-0.5 rounded border p-1 text-left ${empty ? 'border-dashed border-coral/60 bg-white' : 'border-marine/15 bg-white hover:border-marine'}`}>
      <span className={`text-[10px] font-bold ${empty ? 'text-coral' : 'text-ink'}`}>{Number(day.date.slice(8))}</span>
      {SHIFT_TYPE_ORDER.map((type) =>
        day.seats[type] > 0 ? (
          <span key={type} className="flex items-center gap-1 text-[9px] leading-none text-ink-mid">
            <span className="w-6">{SHIFT_TYPE_LABELS[type]}</span>
            <SeatSquares filled={day.filled[type]} seats={day.seats[type]} />
          </span>
        ) : null,
      )}
    </button>
  )
}

/**
 * The month's published shifts as a calendar (month readiness plan): each day's seats by shift type, a day with
 * nothing published outlined in coral, a day opening its shifts. Above it, supply beside demand - the seats of each
 * shift type against how many saved preferences put it first or will work it.
 */
export function PublishedShiftsCalendar({ shifts, preferences, period, fill = NO_SEAT_FILL }: { shifts: readonly AvailableShift[]; preferences: readonly ShiftPreferences[]; period: string; /** Who holds each seat; without it every seat reads open. */ fill?: SeatFill }) {
  const [role, setRole] = useState<string | null>(null)
  const [unitId, setUnitId] = useState<string | null>(null)
  const [openDate, setOpenDate] = useState<string | null>(null)
  const { days, roles, units } = publishedShiftsByDay(shifts, period, { role, unitId }, fill)
  const demand = shiftTypeDemand(days, preferences)
  const leading = new Date(`${period}-01T12:00:00Z`).getUTCDay()
  const open = days.find((day) => day.date === openDate) ?? null
  return (
    <div className="flex flex-col gap-3">
      <table className="w-full max-w-md text-[11px]">
        <thead>
          <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            <th className="py-1 pr-2 font-bold">Shift type</th>
            <th className="py-1 pr-2 text-right font-bold">Seats</th>
            <th className="py-1 pr-2 text-right font-bold">Filled</th>
            <th className="py-1 pr-2 text-right font-bold">First choice</th>
            <th className="py-1 text-right font-bold">Will work it</th>
          </tr>
        </thead>
        <tbody>
          {demand.map((entry) => (
            <tr key={entry.shiftType} className="border-t border-marine/10">
              <td className="py-1 pr-2 font-bold text-ink">{SHIFT_TYPE_LABELS[entry.shiftType]}</td>
              <td className="py-1 pr-2 text-right text-ink">{entry.seats}</td>
              <td className="py-1 pr-2 text-right text-ink">
                {entry.filled}
                {entry.seats > entry.filled && <span className="text-coral"> · {entry.seats - entry.filled} open</span>}
              </td>
              <td className="py-1 pr-2 text-right text-ink-mid">{entry.firstChoice}</td>
              <td className="py-1 text-right text-ink-mid">{entry.willDo}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-[10px] text-ink-mid/80">Demand counts the {preferences.length} {preferences.length === 1 ? 'worker' : 'workers'} who saved preferences for {describePeriod(period)}; assigned tiers ask for nothing.</p>
      <div className="flex flex-wrap gap-3">
        <Pills label="Role" options={roles} value={role} onChange={setRole} />
        <Pills label="Unit" options={units} value={unitId} onChange={setUnitId} />
      </div>
      <SeatKey fill={fill} />
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((weekday) => (
          <div key={weekday} className="text-center text-[9px] font-bold uppercase tracking-wide text-ink-mid">
            {weekday}
          </div>
        ))}
        {Array.from({ length: leading }, (_, index) => (
          <div key={`lead-${index}`} />
        ))}
        {days.map((day) => (
          <DayCell key={day.date} day={day} onOpen={() => setOpenDate(day.date)} />
        ))}
      </div>
      {open && (
        <Modal eyebrow="Published shifts" title={describeDay(open.date)} subtitle={`${open.shifts.length} ${open.shifts.length === 1 ? 'seat' : 'seats'} · ${open.shifts.filter((shift) => fill.byShift.has(shift.id)).length} filled`} onClose={() => setOpenDate(null)} footer={<Button icon={X} variant="secondary" size="sm" onClick={() => setOpenDate(null)}>Close</Button>}>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="text-left text-[9px] font-bold uppercase tracking-wide text-ink-mid">
                <th className="py-1 pr-2 font-bold">Time</th>
                <th className="py-1 pr-2 font-bold">Type</th>
                <th className="py-1 pr-2 font-bold">Unit</th>
                <th className="py-1 pr-2 font-bold">Role</th>
                <th className="py-1 pr-2 font-bold">Credentials</th>
                <th className="py-1 font-bold">Seat</th>
              </tr>
            </thead>
            <tbody>
              {open.shifts.map((shift) => (
                <tr key={shift.id} className="border-t border-marine/10">
                  <td className="py-1 pr-2 text-ink">
                    {shift.startTime}-{shift.endTime}
                  </td>
                  <td className="py-1 pr-2 text-ink-mid">{SHIFT_TYPE_LABELS[shift.period as keyof typeof SHIFT_TYPE_LABELS] ?? shift.period}</td>
                  <td className="py-1 pr-2 text-ink-mid">{shift.unitId}</td>
                  <td className="py-1 pr-2 text-ink-mid">{shift.role ?? 'Any'}</td>
                  <td className="py-1 pr-2 text-ink-mid">{shift.requiredCredentials?.join(', ') || '-'}</td>
                  <td className="py-1">
                    {fill.byShift.has(shift.id) ? (
                      <span className="flex items-center gap-1 text-ink">
                        <span className={`h-2 w-2 rounded-sm border ${SEAT_FILLED}`} /> {fill.byShift.get(shift.id)}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 font-bold text-coral">
                        <span className={`h-2 w-2 rounded-sm border ${SEAT_OPEN}`} /> Open
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  )
}
